import { useState, useCallback } from 'react';

/**
 * Hook for managing a priority-based banner queue.
 * Shows only the highest priority banner at any time.
 * Banners auto-dismiss when their condition is resolved.
 */
export const useBannerQueue = () => {
  const [banners, setBanners] = useState([]);
  
  /**
   * Add a banner to the queue.
   * If a banner of the same type exists, it will be replaced.
   * Banners are sorted by priority (higher = more important).
   */
  const addBanner = useCallback((banner) => {
    setBanners(prev => {
      // Remove any existing banner of the same type
      const filtered = prev.filter(b => b.type !== banner.type);
      
      // Add new banner with unique ID and sort by priority
      const newBanner = { 
        ...banner, 
        id: `${banner.type}-${Date.now()}` 
      };
      
      return [...filtered, newBanner]
        .sort((a, b) => (b.priority || 50) - (a.priority || 50));
    });
  }, []);
  
  /**
   * Remove a banner by type.
   * Called when the condition that triggered the banner is resolved.
   */
  const removeBanner = useCallback((type) => {
    setBanners(prev => prev.filter(b => b.type !== type));
  }, []);
  
  /**
   * Clear all banners.
   */
  const clearAllBanners = useCallback(() => {
    setBanners([]);
  }, []);
  
  /**
   * Get the highest priority banner (the one that should be displayed).
   */
  const activeBanner = banners[0] || null;
  
  return { 
    addBanner, 
    removeBanner, 
    clearAllBanners,
    activeBanner,
    bannerCount: banners.length
  };
};


